import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseResourceComponent, NavigationService } from '@memberjunction/ng-shared';
import { RunQuery, Metadata } from '@memberjunction/core';
import { BCBaseComponent } from '@bluecypress/bcsaas-ng-core';

// ── Types ──

interface OverviewStats {
  totalConversations: number;
  activeOrganizations: number;
  totalOrganizations: number;
  totalAgentRuns: number;
  completedRuns: number;
  failedRuns: number;
  successRate: number;
  avgResponseTimeMs: number;
  totalCost: number;
}

interface OrgActivity {
  organizationID: string;
  organizationName: string;
  conversationCount: number;
  activeUsers: number;
  totalRuns: number;
  successRate: number;
  totalCost: number;
  lastActivityAt: string | null;
}

interface RecentFailure {
  agentRunID: string;
  organizationName: string;
  personName: string;
  errorMessage: string;
  startedAt: string;
  durationMs: number | null;
}

interface DailyTrend {
  day: string;
  conversationCount: number;
  runCount: number;
  failedCount: number;
}

type TimePreset = 'this-month' | 'last-month' | '7d' | 'all-time' | 'custom';

@Component({
  standalone: false,
  selector: 'skip-monitoring-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.css']
})
@RegisterClass(BaseResourceComponent, 'SkipMonitoringOverviewResource')
export class SkipMonitoringOverviewComponent extends BCBaseComponent implements OnInit {
  // Authorization
  IsAuthorizedUser = false;
  IsInitializing = true;

  // Loading
  Loading = false;
  Error: string | null = null;

  // Time selector
  TimePreset: TimePreset = 'this-month';
  CustomStartDate = '';
  CustomEndDate = '';

  // Data
  Stats: OverviewStats | null = null;
  OrgActivities: OrgActivity[] = [];
  RecentFailures: RecentFailure[] = [];
  DailyTrends: DailyTrend[] = [];

  // Computed chart data
  MaxOrgConversations = 0;
  MaxDailyConversations = 0;

  private cdr = inject(ChangeDetectorRef);
  protected override navigationService = inject(NavigationService);

  constructor() {
    super();
  }

  async GetResourceDisplayName(): Promise<string> {
    return 'Skip Monitoring Overview';
  }

  async GetResourceIconClass(): Promise<string> {
    return 'fa-solid fa-gauge-high';
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.IsAuthorizedUser = this.checkAuthorization();
    this.IsInitializing = false;
    this.cdr.detectChanges();

    if (!this.IsAuthorizedUser) {
      this.NotifyLoadComplete();
      return;
    }

    await this.LoadAllData();
    this.NotifyLoadComplete();
  }

  // ========== Authorization ==========

  private checkAuthorization(): boolean {
    const ctx = this.tenantContextService.currentContext;
    const memberships = this.tenantContextService.GetAllMemberships();
    const isSystemUser = !ctx?.personID && memberships.length === 0;
    const currentUser = Metadata.Provider.CurrentUser;
    const hasIntegrationRole = currentUser?.UserRoles?.some(
      r => r.Role?.toLowerCase() === 'integration'
    ) ?? false;
    return isSystemUser || hasIntegrationRole;
  }

  // ========== Time Range ==========

  SetTimePreset(preset: TimePreset): void {
    this.TimePreset = preset;
    this.LoadAllData();
  }

  private getTimeRange(): { start: string; end: string } {
    const now = new Date();
    switch (this.TimePreset) {
      case 'this-month':
        return this.buildThisMonthRange(now);
      case 'last-month':
        return this.buildLastMonthRange(now);
      case '7d':
        return this.buildDaysAgoRange(now, 7);
      case 'all-time':
        return { start: new Date(2020, 0, 1).toISOString(), end: now.toISOString() };
      case 'custom': {
        if (this.CustomStartDate && this.CustomEndDate) {
          return {
            start: new Date(this.CustomStartDate).toISOString(),
            end: new Date(this.CustomEndDate).toISOString()
          };
        }
        return this.buildThisMonthRange(now);
      }
    }
  }

  private buildThisMonthRange(now: Date): { start: string; end: string } {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString(), end: now.toISOString() };
  }

  private buildLastMonthRange(now: Date): { start: string; end: string } {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString(), end: end.toISOString() };
  }

  private buildDaysAgoRange(now: Date, days: number): { start: string; end: string } {
    const start = new Date(now.getTime() - days * 86400000);
    return { start: start.toISOString(), end: now.toISOString() };
  }

  // ========== Data Loading ==========

  async LoadAllData(): Promise<void> {
    this.Loading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const { start, end } = this.getTimeRange();
      const rq = new RunQuery();
      const params = { StartDate: start, EndDate: end };

      const [statsResult, orgResult, failuresResult, trendResult] = await Promise.all([
        rq.RunQuery({ QueryName: 'MonitoringOverviewStats', CategoryPath: '/Skip/Monitoring', Parameters: params }),
        rq.RunQuery({ QueryName: 'MonitoringOrgActivity', CategoryPath: '/Skip/Monitoring', Parameters: params }),
        rq.RunQuery({ QueryName: 'MonitoringRecentFailures', CategoryPath: '/Skip/Monitoring', Parameters: {} }),
        rq.RunQuery({ QueryName: 'MonitoringDailyTrend', CategoryPath: '/Skip/Monitoring', Parameters: params })
      ]);

      this.parseStatsResult(statsResult);
      this.parseOrgActivityResult(orgResult);
      this.parseFailuresResult(failuresResult);
      this.parseTrendResult(trendResult);
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load monitoring data';
    } finally {
      this.Loading = false;
      this.cdr.detectChanges();
    }
  }

  private parseStatsResult(result: { Success: boolean; Results?: Record<string, unknown>[] }): void {
    if (!result.Success || !result.Results?.length) {
      this.Stats = null;
      return;
    }
    const r = result.Results[0];
    this.Stats = {
      totalConversations: (r['TotalConversations'] as number) || 0,
      activeOrganizations: (r['ActiveOrganizations'] as number) || 0,
      totalOrganizations: (r['TotalOrganizations'] as number) || 0,
      totalAgentRuns: (r['TotalAgentRuns'] as number) || 0,
      completedRuns: (r['CompletedRuns'] as number) || 0,
      failedRuns: (r['FailedRuns'] as number) || 0,
      successRate: (r['SuccessRate'] as number) || 0,
      avgResponseTimeMs: (r['AvgResponseTimeMs'] as number) || 0,
      totalCost: (r['TotalCost'] as number) || 0
    };
  }

  private parseOrgActivityResult(result: { Success: boolean; Results?: Record<string, unknown>[] }): void {
    if (!result.Success || !result.Results?.length) {
      this.OrgActivities = [];
      this.MaxOrgConversations = 0;
      return;
    }
    this.OrgActivities = result.Results.map(r => ({
      organizationID: (r['OrganizationID'] as string) || '',
      organizationName: (r['OrganizationName'] as string) || 'Unknown',
      conversationCount: (r['ConversationCount'] as number) || 0,
      activeUsers: (r['ActiveUsers'] as number) || 0,
      totalRuns: (r['TotalRuns'] as number) || 0,
      successRate: (r['SuccessRate'] as number) || 0,
      totalCost: (r['TotalCost'] as number) || 0,
      lastActivityAt: (r['LastActivityAt'] as string) || null
    }));
    this.MaxOrgConversations = Math.max(...this.OrgActivities.map(o => o.conversationCount), 0);
  }

  private parseFailuresResult(result: { Success: boolean; Results?: Record<string, unknown>[] }): void {
    if (!result.Success || !result.Results?.length) {
      this.RecentFailures = [];
      return;
    }
    this.RecentFailures = result.Results.map(r => ({
      agentRunID: (r['AgentRunID'] as string) || '',
      organizationName: (r['OrganizationName'] as string) || 'Unknown',
      personName: (r['PersonName'] as string) || 'Unknown',
      errorMessage: (r['ErrorMessage'] as string) || 'No error message',
      startedAt: (r['StartedAt'] as string) || '',
      durationMs: (r['DurationMs'] as number) || null
    }));
  }

  private parseTrendResult(result: { Success: boolean; Results?: Record<string, unknown>[] }): void {
    if (!result.Success || !result.Results?.length) {
      this.DailyTrends = [];
      this.MaxDailyConversations = 0;
      return;
    }
    this.DailyTrends = result.Results.map(r => ({
      day: (r['Day'] as string) || '',
      conversationCount: (r['ConversationCount'] as number) || 0,
      runCount: (r['RunCount'] as number) || 0,
      failedCount: (r['FailedCount'] as number) || 0
    }));
    this.MaxDailyConversations = Math.max(...this.DailyTrends.map(d => d.conversationCount), 0);
  }

  // ========== Display Helpers ==========

  GetOrgActivityPercent(org: OrgActivity): number {
    if (!this.MaxOrgConversations || !org.conversationCount) return 0;
    return (org.conversationCount / this.MaxOrgConversations) * 100;
  }

  GetOrgActivityClass(org: OrgActivity): string {
    if (!org.lastActivityAt) return 'org-inactive';
    const diffMs = Date.now() - new Date(org.lastActivityAt).getTime();
    const diffDays = diffMs / 86400000;
    if (diffDays <= 7) return 'org-active';
    if (diffDays <= 30) return 'org-recent';
    return 'org-inactive';
  }

  GetSuccessRateClass(): string {
    if (!this.Stats) return '';
    if (this.Stats.successRate >= 90) return 'rate-good';
    if (this.Stats.successRate >= 70) return 'rate-warning';
    return 'rate-error';
  }

  GetTrendBarHeight(trend: DailyTrend): number {
    if (!this.MaxDailyConversations || !trend.conversationCount) return 0;
    return (trend.conversationCount / this.MaxDailyConversations) * 100;
  }

  GetTrendBarFailedHeight(trend: DailyTrend): number {
    if (!this.MaxDailyConversations || !trend.failedCount) return 0;
    return (trend.failedCount / this.MaxDailyConversations) * 100;
  }

  FormatRelativeTime(iso: string | null): string {
    if (!iso) return 'Never';
    const diffMs = Date.now() - new Date(iso).getTime();
    return this.formatMsToDuration(diffMs);
  }

  private formatMsToDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  }

  FormatResponseTime(ms: number): string {
    if (!ms) return '\u2014';
    const totalSec = Math.round(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min > 0) return `${min}m ${sec}s`;
    return `${sec}s`;
  }

  FormatCost(cost: number): string {
    if (!cost) return '$0.00';
    return `$${cost.toFixed(2)}`;
  }

  FormatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
    });
  }

  FormatShortDay(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }

  TruncateError(message: string, maxLen: number = 80): string {
    if (!message) return '\u2014';
    if (message.length <= maxLen) return message;
    return message.substring(0, maxLen) + '...';
  }

  GetPresetLabel(preset: TimePreset): string {
    const labels: Record<TimePreset, string> = {
      'this-month': 'This Month',
      'last-month': 'Last Month',
      '7d': 'Last 7 Days',
      'all-time': 'All Time',
      'custom': 'Custom'
    };
    return labels[preset];
  }

  get TimePresets(): TimePreset[] {
    return ['this-month', 'last-month', '7d', 'all-time', 'custom'];
  }

  ApplyCustomRange(): void {
    this.TimePreset = 'custom';
    this.LoadAllData();
  }
}
